import React, { useState } from "react";
import {
	Typography,
	Box,
	Button,
	TextField,
	Container,
	Rating,
	Alert,
	AlertTitle,
	Paper,
} from "@mui/material";
import { Star, Send } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";

const Feedback = () => {
	const [rating, setRating] = useState(0);
	const [feedbackText, setFeedbackText] = useState("");
	const [suggestions, setSuggestions] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState({
		success: false,
		error: false,
		message: "",
	});
	const { user } = useAuth();

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (rating === 0) {
			setSubmitStatus({
				success: false,
				error: true,
				message: "Please provide a rating before submitting.",
			});
			return;
		}

		setIsSubmitting(true);
		setSubmitStatus({ success: false, error: false, message: "" });

		try {
			const { error } = await supabase.from("feedback").insert({
				user_id: user.id,
				rating,
				feedback_text: feedbackText,
				suggestions,
			});

			if (error) throw error;

			setSubmitStatus({
				success: true,
				error: false,
				message: "Thank you for your feedback!",
			});

			// Reset form
			setRating(0);
			setFeedbackText("");
			setSuggestions("");
		} catch (error) {
			console.error("Error submitting feedback:", error);
			setSubmitStatus({
				success: false,
				error: true,
				message: "Failed to submit feedback. Please try again.",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Container maxWidth="sm" sx={{ py: 2 }}>
			<Paper
				elevation={2}
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 2,
					p: 3,
					borderRadius: 2,
				}}>
				<Typography variant="h5" align="center" color="primary" sx={{ mb: 1 }}>
					We Value Your Feedback
				</Typography>

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						mb: 1,
					}}>
					<Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
						Rate Your Experience
					</Typography>
					<Rating
						name="feedback-rating"
						value={rating}
						onChange={(event, newValue) => {
							setRating(newValue);
						}}
						icon={<Star fill="#ffc107" color="#ffc107" />}
						emptyIcon={<Star color="#e0e0e0" />}
						size="large"
					/>
				</Box>

				{(submitStatus.error || submitStatus.success) && (
					<Alert
						severity={submitStatus.error ? "error" : "success"}
						sx={{ mb: 1 }}>
						{submitStatus.message}
					</Alert>
				)}

				<TextField
					fullWidth
					multiline
					rows={3}
					label="Your Feedback"
					placeholder="Share your experience..."
					value={feedbackText}
					onChange={(e) => setFeedbackText(e.target.value)}
					variant="outlined"
					sx={{ mb: 1 }}
				/>

				<TextField
					fullWidth
					multiline
					rows={3}
					label="Improvement Suggestions"
					placeholder="How can we improve?"
					value={suggestions}
					onChange={(e) => setSuggestions(e.target.value)}
					variant="outlined"
					sx={{ mb: 2 }}
				/>

				<Button
					type="submit"
					variant="contained"
					color="primary"
					disabled={isSubmitting}
					startIcon={<Send size={20} />}
					sx={{
						alignSelf: "center",
						minWidth: 150,
						textTransform: "none",
					}}
					onClick={handleSubmit}>
					{isSubmitting ? "Submitting..." : "Send Feedback"}
				</Button>
			</Paper>
		</Container>
	);
};

export default Feedback;
